import React, { Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Toaster } from "sonner";
import { LanguageProvider } from "@/i18n";
import { CartProvider } from "@/context/CartContext";
import { AuthProvider } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import AuthCallback from "@/components/AuthCallback";
import ProtectedRoute from "@/components/ProtectedRoute";
// Prerendered pilot routes (/, /locales/:slug) are imported EAGERLY. With client-takeover
// (index.js uses createRoot, NOT hydrateRoot for these snapshots) they mount without a <Suspense>
// boundary, so the client render matches the served snapshot with no loader flash.
import Home from "@/pages/Home";
import LocalDetalle from "@/pages/LocalDetalle";

// All non-pilot routes stay code-split (lazy). Each is wrapped in its own <Suspense> below (helper
// `s`); the eager pilot routes render WITHOUT a Suspense wrapper (client-takeover, no hydrateRoot).
const Carta = lazy(() => import("@/pages/Carta"));
const MenusGrupos = lazy(() => import("@/pages/MenusGrupos"));
const Tienda = lazy(() => import("@/pages/Tienda"));
const ProductDetail = lazy(() => import("@/pages/ProductDetail"));
const Reservas = lazy(() => import("@/pages/Reservas"));
const Locales = lazy(() => import("@/pages/Locales"));
const Naguara = lazy(() => import("@/pages/Naguara"));
const Historia = lazy(() => import("@/pages/Historia"));
const Finca = lazy(() => import("@/pages/Finca"));
const Gracias = lazy(() => import("@/pages/Gracias"));
const ConsultaSaldo = lazy(() => import("@/pages/ConsultaSaldo"));
const Legal = lazy(() => import("@/pages/Legal"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const Admin = lazy(() => import("@/pages/Admin"));

// (client-takeover architecture: no route-preload map needed — pilot routes are eager)

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#FBF9F6]">
    <Loader2 className="w-6 h-6 animate-spin text-[#B85C38]" />
  </div>
);

function AppRouter() {
  const location = useLocation();
  // CRITICAL: process OAuth callback (session_id in URL fragment) before anything else
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  // Per-route <Suspense> for LAZY routes only. Prerendered pilot routes (/, /locales/:slug) are
  // eager and rendered WITHOUT a Suspense boundary: their snapshots come from client createRoot
  // serialization (Puppeteer), which does NOT emit React's Suspense hydration comment markers
  // (<!--$-->/<!--/$-->). hydrateRoot needs those markers to hydrate a <Suspense>; their absence
  // made React discard the boundary (render the PageLoader fallback) and report React #418. With
  // no Suspense above the eager route content, hydration matches the snapshot exactly.
  const s = (el) => <Suspense fallback={<PageLoader />}>{el}</Suspense>;
  return (
    <Routes>
      <Route path="/admin/login" element={s(<AdminLogin />)} />
      <Route path="/admin" element={<ProtectedRoute>{s(<Admin />)}</ProtectedRoute>} />
      <Route element={<Layout />}>
        <Route path="/" element={<Home />} />
        <Route path="/carta" element={s(<Carta />)} />
        <Route path="/menus-para-grupos" element={s(<MenusGrupos />)} />
        <Route path="/tienda" element={s(<Tienda />)} />
        <Route path="/producto/:id" element={s(<ProductDetail />)} />
        <Route path="/reservas" element={s(<Reservas />)} />
        <Route path="/locales" element={s(<Locales />)} />
        <Route path="/locales/terramelar" element={<Navigate to="/locales" replace />} />
        <Route path="/locales/:slug" element={<LocalDetalle />} />
        <Route path="/naguara" element={s(<Naguara />)} />
        <Route path="/historia" element={s(<Historia />)} />
        <Route path="/finca" element={s(<Finca />)} />
        <Route path="/gracias" element={s(<Gracias />)} />
        <Route path="/tarjeta" element={s(<ConsultaSaldo />)} />
        <Route path="/legal/:slug" element={s(<Legal />)} />
        <Route path="*" element={s(<NotFound />)} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <CartProvider>
          <BrowserRouter>
            <Toaster position="top-center" richColors />
            <AppRouter />
          </BrowserRouter>
        </CartProvider>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
